import React, { useRef } from 'react';
import { AppliedProjectStat } from '../hydra';

/*
If tags are selected, projects MUST satisfy the following conditions:
- AND across tag categories (TOPIC, ROLE, APPLICATION)
- OR within each category

For example, if selected tags are ["SQL", "Python", "Marketing", "Fintech"]:
- Where "SQL" and "Python" are TOPIC tags
- "Marketing" is a ROLE tag  
- "Fintech" is an APPLICATION tag
- A project must have (SQL OR Python) AND (Marketing) AND (Fintech)

If search query is present, projects MUST have at least one search term in their name or description.

If both search query and tags are present, projects MUST satisfy both conditions.

Projects are ranked by the number of search terms they match.
*/

export default function useProjectSearch({
  searchQuery,
  selectedTags,
  projects,
}: {
  searchQuery: string;
  selectedTags: string[];
  projects: AppliedProjectStat[];
}) {
  const searchTermCache = useRef(new Map<string, AppliedProjectStat[]>());

  const searchTerms = searchQuery
    .trim()
    .toLowerCase()
    .split(/,| /)
    .filter((s) => s.trim() !== '');

  // First, create a map of tag values to their types from all project tags
  const allTagsMap = new Map<string, string>();
  projects.forEach((p) => {
    p.tags.forEach((tag) => {
      allTagsMap.set(tag.value, tag.type);
    });
  });

  // Group selected tags by their type
  const selectedTagsByType = selectedTags.reduce((acc, tagValue) => {
    const tagType = allTagsMap.get(tagValue);
    if (tagType) {
      if (!acc[tagType]) {
        acc[tagType] = [];
      }
      acc[tagType].push(tagValue);
    }
    return acc;
  }, {} as Record<string, string[]>);

  const tagTypesAndValues = Object.entries(selectedTagsByType);

  const filteredProjects = React.useMemo(() => {
    const taggedProjectCandidates =
      selectedTags.length === 0
        ? projects
        : projects.filter((project) => {
            // For each category that has selected tags, check if project has at least one tag from that category
            // All categories must be satisfied (AND logic)
            return tagTypesAndValues.every(([tagType, tagValues]) => {
              return project.tags.some((projectTag) => 
                projectTag.type === tagType && tagValues.includes(projectTag.value)
              );
            });
          });

    if (searchTerms.length === 0) {
      return taggedProjectCandidates;
    }

    const unrankedProjectResults = searchTerms.map((term) => {
      const cacheKey = term + '|||' + selectedTags.join('-');
      if (searchTermCache.current.has(term)) {
        const results = searchTermCache.current.get(cacheKey);
        if (results) {
          return results;
        }
      }
      const results = taggedProjectCandidates.filter((ap) => {
        return (
          ap.name.toLowerCase().includes(term) ||
          ap.description.toLowerCase().includes(term)
        );
      });
      searchTermCache.current.set(cacheKey, results);
      return results;
    });

    const allMatchedProjects = Array.from(
      new Set(unrankedProjectResults.flat())
    );

    const rankedProjects = allMatchedProjects
      .map((ap) => {
        const rank = unrankedProjectResults.reduce((acc, results) => {
          return acc + (results.includes(ap) ? 1 : 0);
        }, 0);
        return { ap, rank };
      })
      .sort((a, b) => b.rank - a.rank)
      .map(({ ap }) => ap);

    return rankedProjects;
  }, [projects, selectedTags, searchTerms, tagTypesAndValues]);

  return filteredProjects;
}
